import { Analytics, initializeAnalytics, isSupported, logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import { app } from '../firebase/config';
import type { ChangeSummary } from './sessionStorageService';

// Google Analytics 4 (measurement ID in firebase/config.js), for the monthly
// usage report. Rules:
// - Only the live site is measured; localhost and other hosts send nothing.
// - Page views are sent here on every route change (the app changes pages
//   without reloading), so the automatic first-load page view is turned off.
// - Never send names, emails or note text. Signed-in visitors are identified
//   only by their anonymous Firebase user ID.
// - Failures are swallowed: analytics must never affect the site.

export const PRODUCTION_HOSTS = [
    'bhaktigranthaseva.com',
    'www.bhaktigranthaseva.com',
    'bhakti-granth-seva.web.app',
    'bhakti-granth-seva.firebaseapp.com',
];

const MAX_PARAM_LENGTH = 100; // GA4 limit for event parameter values

let analyticsPromise: Promise<Analytics | null> | null = null;

const getAnalyticsInstance = (): Promise<Analytics | null> => {
    if (!analyticsPromise) {
        analyticsPromise = (PRODUCTION_HOSTS.includes(window.location.hostname) ? isSupported() : Promise.resolve(false))
            .then(supported => (supported ? initializeAnalytics(app, { config: { send_page_view: false } }) : null))
            .catch(() => null);
    }
    return analyticsPromise;
};

type EventParams = Record<string, string | number | boolean | undefined>;

const clean = (params: EventParams = {}) => Object.fromEntries(
    Object.entries(params)
        .filter(([, value]) => value !== undefined && value !== '')
        .map(([key, value]) => [key, typeof value === 'string' ? value.slice(0, MAX_PARAM_LENGTH) : value])
);

export const trackEvent = async (name: string, params?: EventParams) => {
    try {
        const analytics = await getAnalyticsInstance();
        if (analytics) logEvent(analytics, name, clean(params));
    } catch {
        // Analytics must never break the site
    }
};

export const trackPageView = (path: string) => trackEvent('page_view', {
    page_path: path,
    page_location: `${window.location.origin}${path}`,
    page_title: document.title,
});

// Signed-in state as a user property, plus the anonymous Firebase user ID so a
// person counts once across devices. Called whenever the auth state changes.
export const setAnalyticsUser = async (userId: string | null) => {
    try {
        const analytics = await getAnalyticsInstance();
        if (!analytics) return;
        setUserId(analytics, userId);
        setUserProperties(analytics, { logged_in: userId ? 'yes' : 'no' });
    } catch {
        // Analytics must never break the site
    }
};

// Lecture modal saves: one event per kind of change the user made
export const trackProgressSave = (changes: ChangeSummary, lectureName: string, collectionPath?: string) => {
    const params = { lecture_name: lectureName, course_path: collectionPath?.replace(/^\/+/, '') };
    if (changes.completionStatus?.to === true) trackEvent('lecture_complete', params);
    if (changes.isFavorited?.to === true) trackEvent('favorite_add', params);
    if (changes.notes && changes.notes.to) trackEvent('note_save', params);
};

// For tests only
export const resetAnalyticsForTests = () => {
    analyticsPromise = null;
};
